import { Pagination, Typography } from 'components';
import { ConfigureAttributes, TBody, THead } from 'components-legacy';
import useConfigureVisibleAttributes from 'hooks/useConfigureVisibleAttributes';
import useDragTable from 'hooks/useDragTable';
import useFiltersUrlSync from 'hooks/useFiltersUrlSync';
import { InfoIcon } from 'lib/svgs/Icons';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import 'styles/legacy/modules/datatable/actions_ellipsis.scss';
import 'styles/legacy/modules/datatable/configure_table_icon.scss';

// ABOUT DataTable component:
//
// This component is quite complex already and it is not advisable to continue expanding it
// It is meant to be used as a quick solution for simplest datatable scenarios (so most of them)
// and also as a starting point to fork from for more complex interactive tables.
//
// Obviously if repeating patterns arise from the forks they should be either included here
// or extracted in some other way like a second generic DataTable, hooks or new sub elements.

export default function DataTable({
  // Required
  name,
  keys,
  getQuery,
  updateFilters,
  updatePage,
  columnWidths,
  // Optional
  header,
  subHeader,
  headerClass,
  sortableKeys,
  sortAttribute,
  sortOrder,
  filterUrlSync,
  hiddenKeys = [],
  components = {},
  sortableKeysLabels = (k) => k,
  labels = (k) => k,
  Actions = () => null,
  Filters = () => null
}) {
  useFiltersUrlSync(updateFilters, '', filterUrlSync);

  const filters = useSelector((state) => state.filters);
  const dispatch = useDispatch();
  const { data, isLoading, error } = getQuery(filters);
  const { items, totalPages, totalItems } = data || {};
  const [showConfigurator, setShowConfigurator] = useState(false);
  const { visibleAttributes, setVisibleAttributes } = useConfigureVisibleAttributes(
    name,
    keys,
    hiddenKeys
  );
  const shownKeys = keys.filter((k) => visibleAttributes[k]);
  const { sliderRef, draggable } = useDragTable([visibleAttributes]);

  return (
    <>
      {!!header && (
        <h1 className={headerClass}>
          {header}
          {Number.isInteger(totalItems) && (
            <span className="color-dull ml-05"> ({totalItems}) </span>
          )}
          {subHeader && (
            <span className="flex items-center">
              <InfoIcon className="mr-1 scale-[0.75]" style={{ transform: 'scale(0.75)' }} />
              <Typography variant="light3" className="!m-0 italic">
                <small className="small !italic ">{subHeader}</small>
              </Typography>
            </span>
          )}
        </h1>
      )}

      <Filters />
      <Actions
        shownKeys={shownKeys}
        sortAttribute={sortAttribute}
        sortOrder={sortOrder}
        totalItems={totalItems}
      />

      {showConfigurator && (
        <ConfigureAttributes
          filters={filters}
          keys={keys}
          labels={labels}
          setShowConfigurator={setShowConfigurator}
          setVisibleAttributes={setVisibleAttributes}
          sortableKeys={sortableKeys}
          sortableKeysLabels={sortableKeysLabels}
          updateFilters={(f) => dispatch(updateFilters(f))}
          visibleAttributes={visibleAttributes}
        />
      )}
      <div style={{ width: '100%', overflow: 'auto' }} ref={sliderRef}>
        <table className={`table--auto w-100 table w-full ${draggable && 'table--draggable'}`}>
          <THead
            className={'thead--secondary thead--hoverable'}
            columnWidths={columnWidths}
            keys={shownKeys}
            labels={labels}
            attributeCb={() => setShowConfigurator(true)}
            sortableKeys={sortableKeys}
            sortAttribute={sortAttribute}
            sortOrder={sortOrder}
          />
          <TBody
            items={items}
            isLoading={isLoading}
            keys={shownKeys}
            components={components}
            error={error}
          />
        </table>
      </div>
      <Pagination
        page={filters.page}
        totalPages={totalPages || 0}
        changePage={(p) => dispatch(updatePage(p))}
        className="my-6 flex flex-col items-center"
      />
    </>
  );
}
